import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  RefreshTaxReconInput,
  TaxReconSnapshotResponse,
  TaxReconSummaryResponse,
  UpdateTaxReconInput,
} from '@dcc-erp/shared';

import { api } from '../api';

// Cache-key scope — keyed by companyId per the convention used in
// queries/vat.ts / queries/ar.ts.
const taxReconKey = (companyId: string | undefined) => ['tax-recon', companyId] as const;

export function useTaxReconSnapshot(companyId: string | undefined, period: string | undefined) {
  return useQuery({
    queryKey: [...taxReconKey(companyId), 'snapshot', period],
    queryFn: () =>
      api<TaxReconSnapshotResponse>(`/companies/${companyId}/tax-recon?period=${period}`),
    enabled: !!companyId && !!period && /^\d{6}$/.test(period),
  });
}

export function useTaxReconSummary(companyId: string | undefined, year: number | undefined) {
  return useQuery({
    queryKey: [...taxReconKey(companyId), 'summary', year],
    queryFn: () =>
      api<TaxReconSummaryResponse>(`/companies/${companyId}/tax-recon/summary?year=${year}`),
    enabled: !!companyId && !!year,
  });
}

export function useRefreshTaxRecon(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RefreshTaxReconInput) =>
      api<TaxReconSnapshotResponse>(`/companies/${companyId}/tax-recon/refresh`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taxReconKey(companyId) });
    },
  });
}

export function useUpdateTaxRecon(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaxReconInput }) =>
      api<unknown>(`/companies/${companyId}/tax-recon/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taxReconKey(companyId) });
    },
  });
}
